import * as React from 'react';
import { useDropzone } from 'react-dropzone';

import { Avatar, Box, BoxProps, styled, Alert, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import { environment } from '../../config';
import usePortfolioQuery from '../../pages/Profile/components/usePortfolioQuery';
import Icons from '../../themes/icons';

type PhotoCardProps = BoxProps<'div'> & {
  error?: boolean;
  isDragActive?: boolean;
};

const baseUrl = (() => {
  const envUrl = environment === 'production' ? '' : `-${environment}`;
  return `https://assets${envUrl}.beautypass.app`;
})();

const PhotoCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'error' && prop !== 'isDragActive',
})<PhotoCardProps>(({ isDragActive }) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '8px',
  backgroundColor: isDragActive ? '#e0f7fa' : '#F7F7F7',
  width: '100%',
  cursor: 'pointer',
  minHeight: '100%',
  border: isDragActive ? '2px dashed #00bcd4' : '2px dashed transparent',
  transition: 'all 0.2s ease-in-out',
}));

// Image validation constants
const IMAGE_REQUIREMENTS = {
  RECOMMENDED_WIDTH: 2000,
  RECOMMENDED_HEIGHT: 2000,
  MIN_SIZE: 200 * 1024, // 200KB in bytes
  MAX_SIZE: 5 * 1024 * 1024, // 5MB in bytes
  ACCEPTED_FORMATS: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
};

type ValidationError = {
  type: 'size' | 'dimensions' | 'format';
  message: string;
  severity: 'error' | 'warning';
};

export const PortfolioPhoto: React.FC = () => {
  const { uploadMutation } = usePortfolioQuery();
  const [previewImg, setPreviewImg] = React.useState<string>();
  const [loading, setLoading] = React.useState<boolean>();
  const [validationError, setValidationError] = React.useState<ValidationError | null>(null);

  const validateImage = (file: File): Promise<ValidationError | null> => {
    return new Promise((resolve) => {
      // Check file format
      if (!IMAGE_REQUIREMENTS.ACCEPTED_FORMATS.includes(file.type)) {
        resolve({
          type: 'format',
          message: 'Please upload a valid image format (JPEG, PNG, or WebP)',
          severity: 'error'
        });
        return;
      }

      // Check file size
      if (file.size < IMAGE_REQUIREMENTS.MIN_SIZE) {
        resolve({
          type: 'size',
          message: `File size is too small. Minimum size is ${Math.round(IMAGE_REQUIREMENTS.MIN_SIZE / 1024)}KB`,
          severity: 'error'
        });
        return;
      }

      if (file.size > IMAGE_REQUIREMENTS.MAX_SIZE) {
        resolve({
          type: 'size',
          message: `File size is too large. Maximum size is ${IMAGE_REQUIREMENTS.MAX_SIZE / (1024 * 1024)}MB`,
          severity: 'error'
        });
        return;
      }

      // Check image dimensions
      const img = new Image();
      img.onload = () => {
        const { width, height } = img;

        // Warning for non-optimal dimensions (allows upload but shows warning)
        if (width < IMAGE_REQUIREMENTS.RECOMMENDED_WIDTH * 0.8 || height < IMAGE_REQUIREMENTS.RECOMMENDED_HEIGHT * 0.8) {
          resolve({
            type: 'dimensions',
            message: `For best quality, use ${IMAGE_REQUIREMENTS.RECOMMENDED_WIDTH}x${IMAGE_REQUIREMENTS.RECOMMENDED_HEIGHT}px. Current: ${width}x${height}px`,
            severity: 'warning'
          });
          return;
        }

        resolve(null); // No validation errors
      };

      img.onerror = () => {
        resolve({
          type: 'format',
          message: 'Unable to read image file. Please try a different image.',
          severity: 'error'
        });
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const onDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setValidationError(null);
      setLoading(true);

      try {
        // Validate the image
        const validationResult = await validateImage(file);
        
        // Block upload for errors (not warnings)
        if (validationResult && validationResult.severity === 'error') {
          setValidationError(validationResult);
          setLoading(false);
          return;
        }

        // Show warning but allow upload
        if (validationResult && validationResult.severity === 'warning') {
          setValidationError(validationResult);
        }

        const dataUrl = URL.createObjectURL(file);
        setPreviewImg(dataUrl);

        await uploadMutation.mutateAsync(file);
        
        // Clear preview and warning on successful upload
        setPreviewImg('');
        setValidationError(null);
      } catch (error) {
        console.error('Upload failed:', error);
        setValidationError({
          type: 'size',
          message: 'Upload failed. Please try again or choose a different image.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    },
    [uploadMutation],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open: openFileDialog,
  } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 
      'image/jpeg': [],
      'image/jpg': [],
      'image/png': [],
      'image/webp': []
    },
    noClick: true,
    noKeyboard: true,
  });

  const clearError = () => {
    setValidationError(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PhotoCard
        {...getRootProps()}
        isDragActive={isDragActive}
        onClick={!loading ? openFileDialog : undefined}
        sx={{ mb: validationError ? 2 : 0 }}
      >
        <input {...getInputProps()} disabled={loading} />

        {previewImg && (
          <Avatar
            src={previewImg}
            sx={{
              position: 'absolute',
              borderRadius: 'inherit',
              height: '100%',
              width: '100%',
              opacity: loading ? 0.5 : 1,
            }}
          />
        )}

        {!previewImg && !loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              p: 2,
              textAlign: 'center'
            }}
          >
            <Icons.AddIcon />
          </Box>
        )}

        {loading && <CircularProgress size={20} />}
      </PhotoCard>

      {/* Validation Error Display */}
      {validationError && (
        <Alert 
          severity={validationError.severity} 
          onClose={clearError}
          sx={{ mt: 1 }}
        >
          {validationError.message}
        </Alert>
      )}
    </Box>
  );
};